
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { 
  Bell, Globe, Lock, Mail, Moon, Shield, Smartphone, Sun, 
  Fingerprint, Download, Code, Terminal, Key, Store, Plus, 
  Trash2, RefreshCw, AlertTriangle, ChevronDown, CheckCircle, 
  Activity, Eye, Copy, Zap, User, CreditCard, ShieldCheck, 
  Users, Settings as SettingsIcon, Layout, Database
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../context/ThemeContext';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { EditProfileModal } from './settings/EditProfileModal';
import { CreateKeyModal } from './settings/CreateKeyModal';
import { AnimatePresence, motion } from 'framer-motion';
import { fetchApiKeys, createApiKey, revokeApiKey, rotateApiKey } from '../../services/api';
import { ApiKey } from '../../types';

type SettingsTab = 'profile' | 'account' | 'api' | 'security' | 'appearance';

export const SettingsView: React.FC = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  
  const [notifications, setNotifications] = useState(true);
  const [twoFactor, setTwoFactor] = useState(true);
  const [showEditProfile, setShowEditProfile] = useState(false);
  
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
      loadKeys();
  }, []);

  const loadKeys = async () => {
      const keys = await fetchApiKeys();
      setApiKeys(keys);
  };

  const handleCreateKey = async (data: any) => {
      await createApiKey(data);
      loadKeys();
  };

  const handleRevokeKey = async (id: string) => {
      if (confirm('Revoke this key? All integrated systems will lose access immediately.')) {
          await revokeApiKey(id);
          loadKeys();
      }
  };

  const handleRotateKey = async (id: string) => {
      if (confirm('Rotate this key? The current secret will be invalidated.')) {
          await rotateApiKey(id);
          loadKeys();
      }
  };

  const copyToClipboard = (text: string) => {
      navigator.clipboard.writeText(text);
      setCopiedKey(text);
      setTimeout(() => setCopiedKey(null), 2000);
  };

  const tabs: { id: SettingsTab; label: string; icon: any }[] = [
    { id: 'profile', label: 'Identity', icon: User },
    { id: 'account', label: 'Account', icon: Layout },
    { id: 'api', label: 'API & Dev', icon: Code },
    { id: 'security', label: 'Security', icon: ShieldCheck },
    { id: 'appearance', label: 'Display', icon: Moon },
  ];

  return (
    <div className="max-w-[1400px] mx-auto animate-fade-in pb-24">
      {/* 1. Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-8 border-b border-stone-200 dark:border-white/10 pb-6 px-1">
          <div className="space-y-0.5">
              <h2 className="font-serif text-3xl text-[#0A0B0F] dark:text-white font-bold tracking-tight uppercase tracking-widest">System Configuration</h2>
              <p className="text-stone-500 text-[10px] font-black uppercase tracking-[0.25em] opacity-60">Sovereign Identity Control</p>
          </div>
          <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-sm text-[9px] font-black text-stone-500 uppercase tracking-widest">
                  Verified Node: v1.2.4-STABLE
              </span>
          </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* 2. Side Navigation */}
        <div className="lg:col-span-3 space-y-1">
            {tabs.map((tab) => (
                <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-5 py-3 rounded-sm transition-all group ${
                        activeTab === tab.id 
                        ? 'bg-[#0A0B0F] dark:bg-white text-white dark:text-black shadow-md' 
                        : 'text-stone-500 hover:text-stone-900 dark:hover:text-white hover:bg-stone-50 dark:hover:bg-white/5'
                    }`}
                >
                    <tab.icon size={16} className={activeTab === tab.id ? 'text-nobel-gold' : 'opacity-40 group-hover:opacity-100'} />
                    <span className="text-[10px] font-black uppercase tracking-[0.15em]">{tab.label}</span>
                </button>
            ))}
            
            <div className="pt-8 mt-8 border-t border-stone-100 dark:border-white/5 px-4">
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[9px] font-mono font-bold text-emerald-600 dark:text-emerald-500 uppercase tracking-widest">Encryption: Active</span>
                </div>
            </div>
        </div>

        {/* 3. Content Block */}
        <div className="lg:col-span-9">
            <AnimatePresence mode="wait">
                {activeTab === 'profile' && (
                    <motion.div key="profile" initial={{ opacity: 0, x: 5 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -5 }} className="space-y-6">
                        <Card className="p-8 border-stone-200 dark:border-white/10 relative overflow-hidden bg-white dark:bg-[#15171C] rounded-sm">
                            <div className="absolute top-0 right-0 p-8 opacity-[0.01] pointer-events-none rotate-12">
                                <User size={240} />
                            </div>
                            <div className="flex flex-col md:flex-row items-center gap-10 relative z-10">
                                <div className="relative group">
                                    <div className="w-28 h-28 rounded-sm bg-stone-100 dark:bg-white/5 flex items-center justify-center text-4xl font-serif font-bold text-[#0A0B0F] dark:text-white border border-stone-200 dark:border-white/10 shadow-inner">
                                        {user?.name.charAt(0)}
                                    </div>
                                    <button onClick={() => setShowEditProfile(true)} className="absolute -bottom-2 -right-2 w-9 h-9 bg-[#0A0B0F] dark:bg-white text-nobel-gold dark:text-black rounded-sm shadow-xl flex items-center justify-center border-4 border-white dark:border-[#15171C] hover:scale-105 transition-transform">
                                        <Plus size={16} strokeWidth={3} />
                                    </button>
                                </div>
                                <div className="flex-1 text-center md:text-left space-y-4">
                                    <div>
                                        <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-emerald-500/5 rounded-sm text-[9px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest border border-emerald-500/10 mb-3">
                                            Authenticated Identity
                                        </div>
                                        <h3 className="text-3xl font-serif font-bold text-[#0A0B0F] dark:text-white leading-none">{user?.name}</h3>
                                        <p className="text-xs text-stone-500 font-mono mt-2 tracking-tight">{user?.email || user?.walletAddress}</p>
                                    </div>
                                    <div className="flex flex-wrap justify-center md:justify-start gap-2">
                                        <span className="px-3 py-1 rounded-sm bg-[#0A0B0F] dark:bg-white text-white dark:text-black text-[9px] font-black uppercase tracking-widest">
                                            {user?.role} Tier
                                        </span>
                                        <span className="px-3 py-1 rounded-sm bg-nobel-gold/10 text-nobel-gold text-[9px] font-black uppercase tracking-widest border border-nobel-gold/20">
                                            Mainnet Validated
                                        </span>
                                    </div>
                                </div>
                                <Button variant="outline" onClick={() => setShowEditProfile(true)} className="rounded-sm h-11 px-8 font-black text-[9px] uppercase tracking-widest">Identity Audit</Button>
                            </div>
                        </Card>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Card className="p-6 rounded-sm">
                                <h4 className="font-serif text-lg font-bold mb-5 text-[#0A0B0F] dark:text-white uppercase tracking-widest">Contact Metadata</h4>
                                <div className="space-y-4">
                                    <div className="space-y-1.5">
                                        <div className="text-[9px] font-black text-stone-400 uppercase tracking-widest ml-1">Ledger Address</div>
                                        <div className="font-mono text-[11px] text-[#0A0B0F] dark:text-gray-300 bg-stone-50 dark:bg-black/20 p-3 rounded-sm border border-stone-100 dark:border-white/5 truncate">
                                            {user?.walletAddress || '0x7611...B37C'}
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <div className="text-[9px] font-black text-stone-400 uppercase tracking-widest ml-1">Sovereign Relayer</div>
                                        <div className="font-mono text-[11px] text-[#0A0B0F] dark:text-gray-300 bg-stone-50 dark:bg-black/20 p-3 rounded-sm border border-stone-100 dark:border-white/5 truncate">
                                            {user?.email || 'OFFLINE'}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                            <Card className="p-6 bg-[#0A0B0F] text-white border-white/10 relative overflow-hidden group rounded-sm shadow-md">
                                <div className="absolute top-0 right-0 p-4 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity rotate-12"><Shield size={140}/></div>
                                <h4 className="font-serif text-lg font-bold mb-4 relative z-10 uppercase tracking-widest">Compliance Status</h4>
                                <p className="text-[11px] text-stone-500 leading-relaxed mb-6 relative z-10 font-medium">Verified adherence to Sovereign Digital jurisdiction protocols (SDPF-26).</p>
                                <div className="flex items-center gap-4 relative z-10 p-3 bg-white/5 rounded-sm border border-white/10">
                                    <div className="w-10 h-10 rounded-sm bg-white/5 flex items-center justify-center text-nobel-gold shadow-inner"><ShieldCheck size={20}/></div>
                                    <div>
                                        <div className="text-[9px] font-black uppercase tracking-widest text-stone-500">Security Clearance</div>
                                        <div className="text-xs font-black text-emerald-500 uppercase tracking-widest">TIER-1 PRIVACY</div>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </motion.div>
                )}

                {activeTab === 'api' && (
                    <motion.div key="api" initial={{ opacity: 0, x: 5 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -5 }} className="space-y-6">
                         <Card className="p-6 rounded-sm">
                            <div className="flex items-center justify-between mb-8 border-b border-stone-50 dark:border-white/5 pb-6">
                                <div className="flex items-center gap-4">
                                    <div className="p-2.5 bg-[#0A0B0F] dark:bg-white rounded-sm text-nobel-gold dark:text-black shadow-lg"><Key size={20}/></div>
                                    <div>
                                        <h3 className="font-serif text-xl font-bold text-[#0A0B0F] dark:text-white uppercase tracking-widest">Machine Tokens</h3>
                                        <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">API access authorization layer</p>
                                    </div>
                                </div>
                                <Button size="sm" onClick={() => setShowCreateKey(true)} icon={<Plus size={14}/>} className="rounded-sm shadow-md font-black text-[9px] uppercase tracking-widest h-10 px-6">ISSUE SECRET</Button>
                            </div>
                            
                            <div className="space-y-2">
                                {apiKeys.map(key => (
                                    <div key={key.id} className={`rounded-sm border transition-all duration-300 ${expandedKey === key.id ? 'bg-[#F9F8F4] dark:bg-white/[0.03] border-nobel-gold/50' : 'bg-white dark:bg-white/[0.01] border-stone-100 dark:border-white/5 hover:border-stone-200'}`}>
                                        <div className="p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                                            <div className="flex items-center gap-4 flex-1 min-w-0">
                                                <div className={`w-2 h-2 rounded-full ${key.status === 'active' ? 'bg-emerald-500 shadow-[0_0_8px_#10b981]' : 'bg-red-500'}`}></div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h4 className="font-black text-[10px] text-[#0A0B0F] dark:text-white uppercase tracking-widest truncate">{key.name}</h4>
                                                        <span className="text-[8px] font-black bg-stone-100 dark:bg-white/10 text-stone-500 dark:text-stone-400 px-2 py-0.5 rounded-sm uppercase tracking-tighter">{key.chain}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <code className="text-[10px] font-mono text-stone-400 dark:text-stone-500 bg-stone-50 dark:bg-black/40 px-2 py-1 rounded-sm border border-stone-100 dark:border-white/5">{key.prefix}••••••••</code>
                                                        <button onClick={() => copyToClipboard(key.prefix)} className="text-stone-300 hover:text-nobel-gold transition-colors">
                                                            {copiedKey === key.prefix ? <CheckCircle size={12} className="text-emerald-500"/> : <Copy size={12}/>}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <button onClick={() => setExpandedKey(expandedKey === key.id ? null : key.id)} className="p-2 hover:bg-stone-100 dark:hover:bg-white/5 rounded-sm transition-all">
                                                <ChevronDown size={16} className={`text-stone-400 transition-transform duration-500 ${expandedKey === key.id ? 'rotate-180' : ''}`} />
                                            </button>
                                        </div>
                                        <AnimatePresence>
                                            {expandedKey === key.id && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 border-t border-stone-50 dark:border-white/5 pt-4">
                                                        <div className="space-y-3">
                                                            <div className="text-[9px] font-black text-stone-400 uppercase tracking-widest">Configured Scopes</div>
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {key.scopes.map(scope => (
                                                                    <span key={scope} className="px-2 py-0.5 bg-stone-100 dark:bg-white/5 rounded-sm text-[9px] font-mono font-bold text-stone-600 dark:text-stone-400 uppercase tracking-tighter border border-stone-200 dark:border-white/10">{scope}</span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div className="flex justify-end items-end gap-2">
                                                             <Button size="sm" variant="outline" onClick={() => handleRotateKey(key.id)} className="rounded-sm h-9 px-4 text-[9px] font-black uppercase tracking-widest border-stone-200 dark:border-white/10">ROTATE</Button>
                                                             <Button size="sm" variant="danger" onClick={() => handleRevokeKey(key.id)} className="rounded-sm h-9 px-4 text-[9px] font-black uppercase tracking-widest">REVOKE</Button>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="mt-8 p-6 rounded-sm bg-[#0A0B0F] border border-white/10 text-white relative overflow-hidden group shadow-lg">
                                <div className="absolute top-0 right-0 p-4 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity"><Terminal size={100}/></div>
                                <h4 className="text-[10px] font-black uppercase tracking-[0.3em] mb-4 flex items-center gap-2"><Zap size={12} className="text-nobel-gold"/> Node Implementation</h4>
                                <div className="bg-black/60 rounded-sm p-4 font-mono text-[10px] text-stone-400 border border-white/5 shadow-inner">
                                    <span className="text-stone-600"># Initializing sovereign gateway</span><br/>
                                    const ghost = new GhostPay(&#123; apiKey: <span className="text-nobel-gold">'sk_live_...'</span> &#125;);
                                </div>
                            </div>
                         </Card>
                    </motion.div>
                )}

                {activeTab === 'security' && (
                    <motion.div key="security" initial={{ opacity: 0, x: 5 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -5 }} className="space-y-6">
                         <Card className="p-6 rounded-sm">
                            <div className="flex items-center gap-4 mb-10 border-b border-stone-50 dark:border-white/5 pb-6">
                                <div className="p-2.5 bg-stone-100 dark:bg-white/5 rounded-sm text-stone-900 dark:text-white shadow-inner"><Shield size={20}/></div>
                                <div>
                                    <h3 className="font-serif text-xl font-bold text-[#0A0B0F] dark:text-white uppercase tracking-widest">Security Citadel</h3>
                                    <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">Policy enforcement and access control</p>
                                </div>
                            </div>
                            <div className="space-y-3">
                                 {[
                                    { icon: Fingerprint, title: "Biometric Clearing", desc: "Require FaceID/TouchID for on-chain signatures.", active: true },
                                    { icon: Lock, title: "Two-Factor Auth", desc: "TOTP mandatory for treasury outflows.", active: twoFactor, toggle: () => setTwoFactor(!twoFactor) },
                                    { icon: RefreshCw, title: "Automatic Lock", desc: "Purge session keys after 15m of inactivity.", active: true }
                                 ].map((item, i) => (
                                    <div key={i} className="flex items-center justify-between p-4 rounded-sm border border-stone-100 dark:border-white/5 bg-stone-50/50 dark:bg-white/[0.01] hover:bg-stone-50 transition-all">
                                        <div className="flex items-center gap-4">
                                            <div className="w-9 h-9 rounded-sm bg-white dark:bg-white/5 flex items-center justify-center text-stone-400 shadow-sm"><item.icon size={18}/></div>
                                            <div>
                                                <div className="font-bold text-[#0A0B0F] dark:text-white text-xs uppercase tracking-widest">{item.title}</div>
                                                <div className="text-[10px] text-stone-500 mt-0.5">{item.desc}</div>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={item.toggle}
                                            className={`w-11 h-6 rounded-full p-1 transition-all ${item.active ? 'bg-emerald-500' : 'bg-stone-300 dark:bg-stone-800'}`}
                                        >
                                            <div className={`w-4 h-4 rounded-full bg-white shadow-md transition-transform ${item.active ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                        </button>
                                    </div>
                                 ))}
                            </div>
                         </Card>
                    </motion.div>
                )}

                {activeTab === 'appearance' && (
                    <motion.div key="appearance" initial={{ opacity: 0, x: 5 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -5 }} className="space-y-6">
                         <Card className="p-6 rounded-sm">
                            <div className="flex items-center gap-4 mb-10 border-b border-stone-50 dark:border-white/5 pb-6">
                                <div className="p-2.5 bg-stone-100 dark:bg-white/5 rounded-sm text-stone-900 dark:text-white shadow-inner"><Moon size={20}/></div>
                                <div>
                                    <h3 className="font-serif text-xl font-bold text-[#0A0B0F] dark:text-white uppercase tracking-widest">Interface Mode</h3>
                                    <p className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">Customize the environment aesthetic</p>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-5 rounded-sm border border-stone-100 dark:border-white/5 bg-stone-50/50 dark:bg-white/[0.01]">
                                    <div className="flex items-center gap-4">
                                        <div className="w-9 h-9 rounded-sm bg-white dark:bg-white/5 flex items-center justify-center text-stone-400 shadow-sm">
                                            {theme === 'dark' ? <Moon size={18}/> : <Sun size={18}/>}
                                        </div>
                                        <div>
                                            <div className="font-bold text-[#0A0B0F] dark:text-white text-xs uppercase tracking-widest">Deep Space Mode</div>
                                            <div className="text-[10px] text-stone-500 mt-0.5">Optimized for institutional trading desks.</div>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={toggleTheme}
                                        className={`w-12 h-6 rounded-full p-1 transition-all ${theme === 'dark' ? 'bg-[#0A0B0F] dark:bg-white' : 'bg-stone-300'}`}
                                    >
                                        <div className={`w-4 h-4 rounded-full bg-white dark:bg-stone-900 shadow-lg transition-transform ${theme === 'dark' ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                    </button>
                                </div>
                            </div>
                         </Card>
                    </motion.div>
                )}
            </AnimatePresence>
            
            <div className="flex justify-center pt-8 border-t border-stone-50 dark:border-white/5 mt-8">
                <p className="text-[9px] text-stone-400 font-black uppercase tracking-[0.4em] opacity-40">Session ID: 0x7611...B37C | Encrypted | Mainnet Relay</p>
            </div>
        </div>
      </div>

      <AnimatePresence>
        {showEditProfile && user && (
            <EditProfileModal user={user} onClose={() => setShowEditProfile(false)} />
        )}
        {showCreateKey && (
            <CreateKeyModal onClose={() => setShowCreateKey(false)} onCreate={handleCreateKey} />
        )}
      </AnimatePresence>
    </div>
  );
};
